import React, { useContext, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { usePortal } from "../../context/PortalsContext";
import { HttpErrorPage } from "../../pages/ErrorPages/HttpErrorPage";
import { LayoutControlContext } from "../../context/LayoutControlContext";

export const PortalRouteGuard: React.FC = () => {
  const { portalUid } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { portals } = usePortal();
  const { setIsErrorComponent } = useContext(LayoutControlContext);

  // Get the selected portalUid
  const selectedPortalUid = portals.selectedPortal?.portalUid;

  // Check if the portalUid in the URL exists in the list of portals
  const portalExists = useMemo(() => {
    if (!portalUid) return false;
    return portals.portals?.some((p) => p.portalUid === portalUid) ?? false;
  }, [portalUid, portals.portals]);

  const isBasePortalRoute = pathname === `/portal-configuration/${portalUid}`;

  // Redirect to the correct portal route if the portalUid doesn't exist but there's a selected portal
  const shouldRedirect =
    !portalExists && !isBasePortalRoute && !!selectedPortalUid && !!portalUid;

  // Show 404 if portalUid doesn't exist and either we're on the base portal route or there's no selected portal part of list of portals
  const shouldShow404 =
    !portalExists && (isBasePortalRoute || !selectedPortalUid);

  useEffect(() => {
    if (shouldRedirect && selectedPortalUid && portalUid) {
      const correctedPath = pathname.replace(
        `/portal-configuration/${portalUid}`,
        `/portal-configuration/${selectedPortalUid}`,
      );
      navigate(correctedPath, { replace: true });
    }

    setIsErrorComponent(shouldShow404);

    return () => {
      setIsErrorComponent(false);
    };
  }, [
    shouldRedirect,
    shouldShow404,
    selectedPortalUid,
    portalUid,
    pathname,
    navigate,
    setIsErrorComponent,
  ]);

  if (shouldRedirect) return null;
  if (shouldShow404) return <HttpErrorPage status={404} isNested />;

  return <Outlet />;
};
